import React, { useMemo, useState } from 'react';
import { Button, Meta, Display, Chip } from '../../components/Atoms';
import { LoadingState, ErrorState, EmptyState } from '../../components/states';
import { useMultipleApi, daysSince } from '../../lib/api';
import ScheduleShell, { ScheduleSection } from './ScheduleShell';

/**
 * ScheduleSeasonal - dwa zabiegi sezonowe w jednej sekcji:
 *   1. Ochrona zimowa (rośliny wymagające okrycia)
 *   2. Dzielenie bylin (rośliny ≥ 3 lat)
 *
 * Endpointy:
 *   GET /api/plots/all-with-details
 *   GET /api/auth/profile (dla first_frost_date)
 */

const PROTECTION_CATEGORIES = new Set([
  'flower_perennial', 'flower_bulb', 'fruit_tree', 'fruit_bush',
  'shrub', 'tree', 'rose', 'climber', 'evergreen',
]);

const PROTECTION_LABELS = {
  flower_perennial: 'Byliny',
  flower_bulb: 'Cebulowe',
  fruit_tree: 'Drzewa owocowe',
  fruit_bush: 'Krzewy owocowe',
  shrub: 'Krzewy ozdobne',
  tree: 'Drzewa ozdobne',
  rose: 'Róże',
  climber: 'Pnącza',
  evergreen: 'Iglaki/Zimozielone',
};

const PROTECTION_TIPS = {
  flower_perennial: 'Okryj liściem lub korą sosnową (5-10 cm). Wrażliwe gatunki (np. funkia) — agrowłókniną.',
  flower_bulb: 'Cebule mieczyków, dalii, georginii — wykop po pierwszym przymrozku, przechowuj w piwnicy.',
  fruit_tree: 'Pnie pobiel wapnem (ochrona przed pęknięciami od mrozu), młode drzewka owiń słomą lub matami.',
  fruit_bush: 'Borówki amerykańskie — agrowłóknina. Maliny — ścinanie pędów na pół.',
  shrub: 'Wrażliwe (hortensje, róże) — kopczykowanie korą + okrycie agrowłókniną.',
  tree: 'Młode (do 3 lat) — owijanie pnia matą słomianą. Korony lekko podwiązane.',
  rose: 'Kopczykowanie korą lub ziemią (15-20 cm) + okrycie chrustem albo stroiszem.',
  climber: 'Zdjąć z podpór, ułożyć na ziemi, przykryć liściem. Wrażliwe — agrowłókniną.',
  evergreen: 'Cisy, tuje — owijanie matą jutową przed pierwszymi mrozami (ochrona przed wysuszeniem).',
};

function ProtectionGroup({ category, plants }) {
  const label = PROTECTION_LABELS[category] || category;
  const tip = PROTECTION_TIPS[category];

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 12,
        marginBottom: 12, paddingBottom: 8,
        borderBottom: '1px solid var(--t-line-2)',
      }}>
        <Display size={16} style={{ display: 'inline' }}>{label}</Display>
        <Meta>{plants.length} {plants.length === 1 ? 'roślina' : 'roślin'}</Meta>
      </div>
      {tip && (
        <div style={{
          padding: '10px 14px', borderRadius: 8,
          background: 'rgba(126, 155, 196, 0.06)',
          borderLeft: '3px solid #7e9bc4',
          fontSize: 13, color: 'var(--t-text-dim)', lineHeight: 1.45,
          marginBottom: 12,
        }}>{tip}</div>
      )}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 8,
      }}>
        {plants.map(p => (
          <div key={p.id} style={{
            padding: '10px 12px', borderRadius: 8,
            background: 'var(--t-bg-elev)',
            border: '1px solid var(--t-line)',
            fontSize: 13, color: 'var(--t-text)',
          }}>
            <div>{p.plant_name}{p.plant_variety && <span style={{ color: 'var(--t-text-dim)' }}> ({p.plant_variety})</span>}</div>
            <div style={{ fontSize: 11, color: 'var(--t-text-mute)', marginTop: 2 }}>
              {p.plot_name} · Rząd {p.row_number}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PropagationCard({ plant, age, urgency }) {
  const tone = urgency === 'urgent' ? 'rust' : urgency === 'soon' ? 'amber' : 'sage';
  const label = urgency === 'urgent' ? 'Pilne (5+ lat)' :
                urgency === 'soon' ? 'Wkrótce (3-4 lata)' :
                urgency === 'good' ? 'Młode (<3 lat)' : 'Wiek nieznany';
  return (
    <div style={{
      padding: '14px 16px', borderRadius: 10,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      borderLeft: `3px solid var(--t-${tone === 'dim' ? 'line-2' : tone})`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Display size={15} style={{ letterSpacing: '-.01em' }}>{plant.plant_name}</Display>
        <Chip tone={tone}>{label}</Chip>
      </div>
      <div style={{ fontSize: 11, color: 'var(--t-text-mute)' }}>
        {plant.plot_name} · Rząd {plant.row_number}
        {age !== null && ` · ${age} ${age === 1 ? 'rok' : age < 5 ? 'lata' : 'lat'}`}
      </div>
    </div>
  );
}

export default function ScheduleSeasonal() {
  const [tab, setTab] = useState('winter'); // 'winter' | 'propagation'
  const { results, loading, errors } = useMultipleApi({
    plots: '/api/plots/all-with-details',
    profile: '/api/auth/profile',
  });

  const allBeds = useMemo(() => {
    if (!results.plots) return [];
    return results.plots.flatMap(p =>
      (p.beds || []).map(b => ({ ...b, plot_name: p.name }))
    );
  }, [results.plots]);

  const protectionByCat = useMemo(() => {
    const groups = {};
    allBeds.forEach(b => {
      if (PROTECTION_CATEGORIES.has(b.plant_category)) {
        const cat = b.plant_category;
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(b);
      }
    });
    return groups;
  }, [allBeds]);

  const perennials = useMemo(() => {
    return allBeds
      .filter(b => b.plant_category === 'flower_perennial')
      .map(b => {
        const age = b.planted_date ? Math.floor(-daysSince(b.planted_date) / 365) : null;
        let urgency = 'unknown';
        if (age !== null) {
          if (age >= 5) urgency = 'urgent';
          else if (age >= 3) urgency = 'soon';
          else urgency = 'good';
        }
        return { ...b, age, urgency };
      });
  }, [allBeds]);

  const error = errors.plots;
  if (error?.includes('autoryz') || error === 'Brak tokenu autoryzacji') {
    return <ScheduleShell><ErrorState error={{ status: 401, message: error }} /></ScheduleShell>;
  }
  if (loading) return <ScheduleShell><LoadingState /></ScheduleShell>;

  const profile = results.profile || {};
  const totalProtection = Object.values(protectionByCat).reduce((s, a) => s + a.length, 0);
  const urgentCount = perennials.filter(p => p.urgency === 'urgent').length;
  const soonCount = perennials.filter(p => p.urgency === 'soon').length;

  // Sezon — październik-listopad: ochrona, marzec-kwiecień: usuwanie / dzielenie
  const month = new Date().getMonth() + 1;
  const isProtectionSeason = month >= 10 || month <= 3;
  const isPropagationSeason = (month >= 3 && month <= 4) || (month >= 9 && month <= 10);

  let daysToFrost = null;
  if (profile.first_frost_date) {
    const frost = new Date(profile.first_frost_date);
    const today = new Date();
    daysToFrost = Math.round((frost - today) / 86400000);
  }

  return (
    <ScheduleShell
      eyebrow="Zabiegi sezonowe"
      title="Zima i dzielenie bylin"
      subtitle="Dwie rzeczy, które robisz raz w sezonie: zabezpieczasz wrażliwe rośliny przed mrozem i dzielisz byliny, żeby zachowały siłę."
    >
      {/* Tab switch */}
      <div style={{
        display: 'flex', gap: 8, padding: 4,
        background: 'var(--t-bg-elev)', borderRadius: 10,
        border: '1px solid var(--t-line)',
        marginBottom: 28, width: 'fit-content',
      }}>
        {[
          { k: 'winter',      label: `Ochrona zimowa (${totalProtection})` },
          { k: 'propagation', label: `Dzielenie bylin (${perennials.length})` },
        ].map(opt => (
          <button
            key={opt.k}
            type="button"
            onClick={() => setTab(opt.k)}
            style={{
              padding: '8px 14px', borderRadius: 8,
              background: tab === opt.k ? 'var(--t-bg)' : 'transparent',
              border: 'none', cursor: 'pointer',
              color: tab === opt.k ? 'var(--t-text)' : 'var(--t-text-dim)',
              fontFamily: 'var(--t-sans)', fontSize: 13,
              fontWeight: tab === opt.k ? 500 : 400,
            }}
          >{opt.label}</button>
        ))}
      </div>

      {tab === 'winter' && (
        <>
          {/* Sezon alert */}
          <div style={{
            padding: '14px 16px', borderRadius: 10,
            background: isProtectionSeason
              ? 'rgba(126, 155, 196, 0.08)'
              : 'rgba(168, 196, 158, 0.06)',
            borderLeft: `3px solid ${isProtectionSeason ? '#7e9bc4' : 'var(--t-sage)'}`,
            marginBottom: 24, fontSize: 13, color: 'var(--t-text)',
          }}>
            {isProtectionSeason
              ? '❄️ Sezon ochrony — okryj wrażliwe rośliny przed pierwszymi mrozami.'
              : '🌱 Poza sezonem ochrony zimowej — możesz przygotować plan na jesień.'}
            {daysToFrost !== null && daysToFrost > 0 && (
              <div style={{ marginTop: 4, fontSize: 12, color: 'var(--t-text-dim)' }}>
                Pierwszy przymrozek za <strong style={{ color: 'var(--t-text)' }}>{daysToFrost}</strong> dni
                ({profile.first_frost_date}).
              </div>
            )}
          </div>

          {totalProtection === 0 ? (
            <EmptyState
              title="Brak roślin wymagających ochrony"
              description="Dodaj byliny, krzewy lub drzewa do swoich grządek — pojawią się tutaj z konkretnymi wskazówkami."
            />
          ) : (
            <ScheduleSection title="Rośliny pogrupowane po typie">
              {Object.entries(protectionByCat).map(([cat, plants]) => (
                <ProtectionGroup key={cat} category={cat} plants={plants} />
              ))}
            </ScheduleSection>
          )}
        </>
      )}

      {tab === 'propagation' && (
        <>
          <div style={{
            padding: '14px 16px', borderRadius: 10,
            background: isPropagationSeason
              ? 'rgba(168, 196, 158, 0.08)'
              : 'rgba(220, 180, 100, 0.06)',
            borderLeft: `3px solid var(--t-${isPropagationSeason ? 'sage' : 'amber'})`,
            marginBottom: 24, fontSize: 13, color: 'var(--t-text)',
          }}>
            {isPropagationSeason
              ? '🌱 Sezon dzielenia — wiosna lub wczesna jesień to najlepszy czas.'
              : '⏳ Poza sezonem — zaplanuj dzielenie na wiosnę (III-IV) lub jesień (IX-X).'}
            {(urgentCount > 0 || soonCount > 0) && (
              <div style={{ marginTop: 4, fontSize: 12, color: 'var(--t-text-dim)' }}>
                <strong style={{ color: urgentCount > 0 ? 'var(--t-rust)' : 'var(--t-amber)' }}>
                  {urgentCount + soonCount}
                </strong> bylin{urgentCount + soonCount === 1 ? 'a wymaga' : ' wymaga'} dzielenia.
              </div>
            )}
          </div>

          {perennials.length === 0 ? (
            <EmptyState
              title="Brak bylin"
              description="Dodaj byliny do swoich grządek — pojawią się tutaj z informacją o wieku i pilności dzielenia."
            />
          ) : (
            <>
              {['urgent', 'soon', 'good', 'unknown'].map(urgency => {
                const list = perennials.filter(p => p.urgency === urgency);
                if (list.length === 0) return null;
                const sectionLabel = urgency === 'urgent' ? 'Pilne (5+ lat)' :
                                     urgency === 'soon' ? 'Wkrótce (3-4 lata)' :
                                     urgency === 'good' ? 'Młode (<3 lat)' : 'Wiek nieznany';
                return (
                  <ScheduleSection key={urgency} title={sectionLabel}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                      gap: 10,
                    }}>
                      {list.map(p => (
                        <PropagationCard key={p.id} plant={p} age={p.age} urgency={p.urgency} />
                      ))}
                    </div>
                  </ScheduleSection>
                );
              })}
            </>
          )}
        </>
      )}
    </ScheduleShell>
  );
}
